import { Select, SelectItem } from '@nextui-org/react'
import React, { memo } from 'react'

// When a Select has an embedded search input (listProps.topContent), NextUI's
// listbox grabs focus on open, so the search input never receives focus and the
// user can't type until they click it. Focus the search input when the dropdown
// opens, retrying across a few frames so we win against react-aria's one-time
// listbox autofocus and the popover open animation.
const focusEmbeddedSearchInput = () => {
  let attempts = 0
  const tryFocus = () => {
    const searchInput = document.querySelector(
      'input[placeholder*="Search"]'
    ) as HTMLInputElement | null
    if (searchInput && document.activeElement !== searchInput) {
      searchInput.focus({ preventScroll: true })
    }
    attempts += 1
    if (attempts < 15 && document.activeElement !== searchInput) {
      requestAnimationFrame(tryFocus)
    }
  }
  requestAnimationFrame(tryFocus)
}

export interface SelectItemType {
  value: string
  label: string
  description?: string
  icon?: React.ReactNode
  disabled?: boolean
}

interface SimpleSelectProps {
  className?: string
  placeholder?: string
  ariaLabel?: string
  size?: 'sm' | 'md' | 'lg'
  selectedKeys: string[]
  onSelectionChange: (selections: string[]) => void
  disabled?: boolean
  readOnly?: boolean
  label?: string
  startContents?: React.ReactNode[]
  isMultiSelect?: boolean
  items: SelectItemType[]
  maxSelectedItems?: number
  /** Hide the "Maximum N selections" helper text (e.g. when the limit is already in the placeholder). */
  hideMaxSelectionHint?: boolean
  listProps?: {
    topContent?: React.ReactNode
  }
}

// Memoize the component to prevent unnecessary re-renders
export const SimpleMultiSelect = memo(function SimpleMultiSelect({
  className,
  ariaLabel,
  placeholder,
  size = 'sm',
  selectedKeys,
  onSelectionChange,
  disabled = false,
  readOnly = false,
  label = '',
  isMultiSelect = false,
  items,
  maxSelectedItems,
  hideMaxSelectionHint = false,
  listProps
}: SimpleSelectProps) {
  const handleSelectionChange = (keys: Set<string | number>) => {
    if (readOnly) return

    // Convert to string array
    const newSelection = Array.from(keys).map(key => String(key))

    // If not multiselect, ensure we only have the last selected item
    let finalSelection = isMultiSelect
      ? newSelection
      : newSelection.length > 0
        ? [newSelection[newSelection.length - 1]]
        : []

    // Apply maxSelectedItems limit if specified
    if (isMultiSelect && maxSelectedItems && finalSelection.length > maxSelectedItems) {
      // If trying to add a new item beyond the limit, keep the most recently selected items
      // up to the maximum limit
      finalSelection = finalSelection.slice(-maxSelectedItems)
    }

    // Only update if there's a change
    if (JSON.stringify(finalSelection) !== JSON.stringify(selectedKeys)) {
      onSelectionChange(finalSelection)
    }
  }

  // Determine which items should be disabled based on selection limit
  const getDisabledKeys = () => {
    if (readOnly) {
      return items.map(item => item.value)
    }

    if (isMultiSelect && maxSelectedItems && selectedKeys.length >= maxSelectedItems) {
      // Disable all items that are not already selected
      return items.filter(item => !selectedKeys.includes(item.value)).map(item => item.value)
    }

    return []
  }

  return (
    <Select
      labelPlacement='inside'
      radius='md'
      size={size}
      label={label}
      placeholder={placeholder}
      aria-label={ariaLabel || label || placeholder || 'Select option'}
      className={className}
      selectedKeys={new Set(selectedKeys)}
      onSelectionChange={keys => {
        if (keys instanceof Set) {
          handleSelectionChange(keys)
        }
      }}
      isDisabled={disabled}
      disabledKeys={getDisabledKeys()}
      selectionMode={isMultiSelect ? 'multiple' : 'single'}
      onOpenChange={isOpen => {
        if (isOpen && listProps?.topContent) {
          focusEmbeddedSearchInput()
        }
      }}
      classNames={{
        base: 'w-full border-box dark:bg-medium6 pb-4 px-[1px]',
        trigger: `w-full h-full bg-white dark:bg-medium6 data-[hover=true]:bg-light3
          dark:data-[hover=true]:bg-medium2 shadow-sm ring-1 ring-[#d4dce2]
          group-data-[focus=true]:bg-transparent rounded-[20px] select-none
          ${readOnly ? 'opacity-70' : ''}`,
        innerWrapper: 'truncate',
        popoverContent: 'rounded-xl text-xs dark:bg-medium6',
        label: 'text-sm font-["Geist"] select-none',
        value: 'text-[14px] font-[400] font-["Geist"] select-none',
        listbox: 'px-3',
        selectorIcon: 'select-none',
        description: 'select-none'
      }}
      description={
        !hideMaxSelectionHint && isMultiSelect && maxSelectedItems
          ? `Maximum ${maxSelectedItems} selection${maxSelectedItems !== 1 ? 's' : ''}`
          : undefined
      }
      listboxProps={
        listProps
          ? {
              topContent: listProps.topContent
            }
          : undefined
      }
    >
      {items.map(item => (
        <SelectItem
          key={item.value}
          value={item.value}
          classNames={{
            base: `px-0 py-2 ${readOnly ? 'cursor-not-allowed' : 'data-[hover=true]:bg-[#f7f7f7] dark:data-[hover=true]:bg-gray-800'}`,
            title: 'text-[14px] font-[400] font-["Geist"] select-none',
            description: 'select-none',
            wrapper: 'flex !flex-row items-center gap-2',
            selectedIcon: '!order-first'
          }}
          startContent={item.icon}
          description={item.description}
          onMouseDown={e => {
            // Prevent the search input from losing focus when clicking on items
            const searchInput = document.querySelector(
              'input[placeholder*="Search"]'
            ) as HTMLInputElement
            if (searchInput && document.activeElement === searchInput) {
              e.preventDefault()
              // Use requestAnimationFrame and preventScroll to avoid scroll jumping
              requestAnimationFrame(() => {
                if (searchInput) {
                  searchInput.focus({ preventScroll: true })
                }
              })
            }
          }}
        >
          {item.label}
        </SelectItem>
      ))}
    </Select>
  )
})
